import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import FormField from '../components/FormField';
import { toast } from '../utils/toast';

const SCHEMA_TYPES   = [{ value:'Employee', label:'Employee' }, { value:'Retainer', label:'Retainer / Consultant' }];
const STATUS_OPTIONS = [{ value:'Active', label:'Active' }, { value:'Inactive', label:'Inactive' }, { value:'Draft', label:'Draft' }];
const CATEGORY_OPTIONS = [
  { value:'Earning',              label:'Earning' },
  { value:'Deduction',            label:'Deduction' },
  { value:'EmployerContribution', label:'Employer Contribution' },
  { value:'Tax',                  label:'Tax' },
  { value:'Subtotal',             label:'Subtotal (display only)' },
];
const PAY_TYPE_OPTIONS = [
  { value:'Fixed', label:'Fixed' }, { value:'Variable', label:'Variable' },
  { value:'Tax-Direct', label:'Tax — Direct' }, { value:'Tax-Indirect', label:'Tax — Indirect' },
];
const BORNE_OPTIONS = [{ value:'Employee', label:'Employee' }, { value:'Company', label:'Company' }];

const CATEGORY_COLOR = {
  Earning:              { bg:'rgba(16,185,129,0.12)', color:'#10B981' },
  Deduction:            { bg:'rgba(239,68,68,0.12)',  color:'#EF4444' },
  EmployerContribution: { bg:'rgba(59,143,244,0.12)', color:'#3B8FF4' },
  Tax:                  { bg:'rgba(245,158,11,0.12)', color:'#F59E0B' },
  Subtotal:             { bg:'rgba(148,163,184,0.15)',color:'#94A3B8' },
};

/* ─────────────────────────── formula evaluator ─────────────────────────── */
/* New regime tax slabs (FY 25-26 / 26-27 baseline). Standard deduction 75,000. */
function slabTaxNewRegime(taxable) {
  const x = Math.max(0, Number(taxable) - 75000);  // std deduction
  if (x <= 0) return 0;
  // Rebate u/s 87A up to Rs 7L → tax is zero there (new regime). For simulation we mimic.
  if (x <= 700000) return 0;
  const slabs = [
    [400000,  0.00],   // 0-4L
    [800000,  0.05],   // 4-8L
    [1200000, 0.10],   // 8-12L
    [1600000, 0.15],   // 12-16L
    [2000000, 0.20],   // 16-20L
    [2400000, 0.25],   // 20-24L
    [Infinity,0.30],   // >24L
  ];
  let prev = 0, tax = 0;
  for (const [cap, rate] of slabs) {
    if (x > cap) { tax += (cap - prev) * rate; prev = cap; }
    else { tax += (x - prev) * rate; break; }
  }
  return tax * 1.04;  // 4% cess
}

function evalFormula(expr, ctx) {
  if (!expr) return 0;
  // Very conservative: only allow digits, whitespace, basic operators, dots, underscores, word chars, parens.
  if (!/^[\w\d\s+\-*/().,%?:><=!|&]*$/.test(expr)) return 0;
  try {
    // Expose ctx vars + math helpers into the function body.
    const names = Object.keys(ctx);
    const vals  = names.map(k => ctx[k]);
    // eslint-disable-next-line no-new-func
    const fn = new Function(...names, 'min', 'max', 'round', 'slab_tax', 'return (' + expr + ');');
    const v = fn(...vals, Math.min, Math.max, Math.round, slabTaxNewRegime);
    return Number.isFinite(Number(v)) ? Number(v) : 0;
  } catch (_) { return 0; }
}

/* Resolver-first, formula-fallback (mirrors Pricing simulator).
 *
 * If the resolver returned a master rate for a component, prefer it over
 * the component's formula. Master rate_pct is interpreted as a percentage
 * of the monthly base (CTC / 12 for Employee schemas, monthly_fees for
 * Retainer schemas). Master fixed_value is taken as a flat monthly amount.
 *
 * Components without a master keep evaluating their formula as before.
 *
 * `userInputs.basic_monthly` (if > 0) overrides the first component's
 * value. The first row in a salary schema is conventionally "Basic" and
 * downstream components reference it; this lets the simulator treat the
 * Basic value as a direct entry instead of computing it from a formula. */
function runSimulation(components, userInputs, resolvedById = {}) {
  const ctx = {
    ctc_annual: Number(userInputs.ctc_annual || 0),
    monthly_fees: Number(userInputs.monthly_fees || 0),
    fees: Number(userInputs.monthly_fees || 0),
    annual_taxable: 0,
    gross: 0, total_deductions: 0, total_tax: 0, net: 0, company_cost: 0,
  };
  // Monthly base used to apply rate_pct from a master.
  const monthlyBase = userInputs.monthly_fees
    ? Number(userInputs.monthly_fees)
    : Number(userInputs.ctc_annual || 0) / 12;

  // Optional override: explicit BASIC value per month, set via the
  // simulator's "Basic Value (per month)" input. When > 0, it replaces
  // whatever formula or master would have produced for the first row.
  const basicMonthly = Number(userInputs.basic_monthly || 0);

  const ordered = [...components].sort((a,b) => (Number(a.seq)||0) - (Number(b.seq)||0));
  const results = [];
  for (let idx = 0; idx < ordered.length; idx++) {
    const c = ordered[idx];
    const key = String(c.code || '').toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const master = resolvedById[c.id];
    const hasMaster = master && !master.no_master &&
                      (master.rate_pct != null || master.fixed_value != null);

    let value;
    let _source;
    let _matchLevel = null;
    if (idx === 0 && basicMonthly > 0) {
      // First component (typically BASIC) uses the user-entered value.
      _source = 'manual_basic';
      value = basicMonthly;
    } else if (hasMaster) {
      _source = 'master';
      _matchLevel = master.match_level;
      if (master.rate_pct != null) {
        value = monthlyBase * (Number(master.rate_pct) || 0) / 100;
      } else {
        value = Number(master.fixed_value) || 0;
      }
    } else {
      _source = c.formula ? 'formula' : 'none';
      value = evalFormula(c.formula, ctx);
    }
    if (key) ctx[key] = value;
    ctx.annual_taxable = ctx.ctc_annual;  // simple baseline for TDS
    results.push({ ...c, _computed: value, _source, _matchLevel });
    if (c.category === 'Earning')              ctx.gross            += value * 12;
    if (c.category === 'Deduction')            ctx.total_deductions += value * 12;
    if (c.category === 'Tax')                  ctx.total_tax        += value * 12;
    if (c.category === 'EmployerContribution') ctx.company_cost     += value * 12;
  }
  ctx.net = ctx.gross - ctx.total_deductions - ctx.total_tax;
  ctx.ctc = ctx.gross + ctx.company_cost;
  return { results, totals: ctx };
}

/* Trim ISO timestamps down to yyyy-MM-dd for HTML date inputs */
const dateOnly = (v) => {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const fmtINR = (n) => {
  if (!Number.isFinite(Number(n))) return '—';
  return '₹ ' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

/* ─────────────────────────────── Main component ─────────────────────────────── */

export default function SalarySchemas() {
  const [mode, setMode]             = useState('list');  // 'list' | 'edit'
  const [schemas, setSchemas]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editRow, setEditRow]       = useState(null);
  const [components, setComponents] = useState([]);
  const [saving, setSaving]         = useState(false);
  const [err, setErr]               = useState('');
  const [search, setSearch]         = useState('');
  // Simulation inputs
  const [simCtc, setSimCtc]         = useState(1500000);
  const [simFees, setSimFees]       = useState(150000);
  const [simBasic, setSimBasic]     = useState(0);          // Basic per month override (0 = ignore)
  const [simView, setSimView]       = useState('yearly');   // 'monthly' | 'yearly'
  const [showSim, setShowSim]       = useState(true);
  const [resolved, setResolved]     = useState([]);   // resolver output per component

  const fetchSchemas = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/salary-schemas');
      if (r.ok) { const d = await r.json(); setSchemas(d.schemas || []); }
    } catch (_) {}
    setLoading(false);
  }, []);
  useEffect(() => { fetchSchemas(); }, [fetchSchemas]);

  async function openNew() {
    setEditRow({ _new:true, code:'', name:'', description:'', schema_type:'Employee',
                 valid_from:'', valid_to:'', status:'Active' });
    setComponents([]); setErr(''); setMode('edit');
  }
  async function openEdit(row) {
    setErr('');
    try {
      const r = await apiFetch(`/api/salary-schemas/${row.id}`);
      if (r.ok) {
        const d = await r.json();
        const schema = { ...d.schema, valid_from: dateOnly(d.schema.valid_from), valid_to: dateOnly(d.schema.valid_to) };
        setEditRow(schema);
        setComponents((d.components || []).map(c => ({ ...c, _key:c.id })));
      } else {
        setEditRow({ ...row, valid_from: dateOnly(row.valid_from), valid_to: dateOnly(row.valid_to) }); setComponents([]);
      }
    } catch (_) { setEditRow({ ...row, valid_from: dateOnly(row.valid_from), valid_to: dateOnly(row.valid_to) }); setComponents([]); }
    // Pull resolver output (no employee context, only L2 rows tagged with
    // settings.company_region_id can match here) so the simulator below
    // uses master rates over formulas.
    try {
      const rr = await apiFetch(`/api/salary-schemas/${row.id}/resolve`);
      if (rr.ok) { const dd = await rr.json(); setResolved(dd.components || []); }
      else setResolved([]);
    } catch (_) { setResolved([]); }
    setMode('edit');
  }

  async function handleSave() {
    if (!editRow) return;
    if (!editRow.code || !editRow.name) { setErr('Code and Name are required'); return; }
    setSaving(true); setErr('');
    try {
      let schemaId = editRow.id;
      if (editRow._new) {
        const r = await apiFetch('/api/salary-schemas', { method:'POST', body: JSON.stringify(editRow) });
        if (!r.ok) { const d = await r.json(); setErr(d.error || 'Save failed'); setSaving(false); return; }
        const d = await r.json(); schemaId = d.id;
      } else {
        const r = await apiFetch(`/api/salary-schemas/${editRow.id}`, { method:'PUT', body: JSON.stringify(editRow) });
        if (!r.ok) { const d = await r.json(); setErr(d.error || 'Save failed'); setSaving(false); return; }
      }
      // Coerce numeric strings before sending so Postgres INT/DECIMAL cols don't explode
      const payload = components.map(c => ({
        ...c,
        seq: c.seq === '' || c.seq === null ? null : Number(c.seq),
        tax_limit: c.tax_limit === '' || c.tax_limit === null ? null : Number(c.tax_limit),
      }));
      const r2 = await apiFetch(`/api/salary-schemas/${schemaId}/components`, {
        method:'POST', body: JSON.stringify(payload),
      });
      if (!r2.ok) { const d = await r2.json(); setErr(d.error || 'Component save failed'); setSaving(false); return; }
      await fetchSchemas();
      setMode('list');
    } catch (e) { setErr('Network error: ' + e.message); }
    setSaving(false);
  }

  async function handleDelete(row) {
    if (!(await toast.confirm({ body: `Delete schema "${row.name}"? All components will be lost.`, danger: true }))) return;
    const r = await apiFetch(`/api/salary-schemas/${row.id}`, { method:'DELETE' });
    if (r.ok) fetchSchemas(); else toast.error('Delete failed')
  }

  async function handleDuplicate(row) {
    const ok = await toast.confirm({
      title: 'Copy schema?',
      body: `This creates a new schema "${row.name} (Copy)" with all components and formulas duplicated. Status starts as Draft.`,
      confirmLabel: 'Copy schema',
    });
    if (!ok) return;
    const r = await apiFetch(`/api/salary-schemas/${row.id}/duplicate`, { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (r.ok) {
      toast.success(`Copied ${d.components_copied || 0} component(s) into "${d.name}"`);
      fetchSchemas();
    } else { toast.error(d.error || 'Copy failed'); }
  }

  /* Component editor helpers */
  let _key = 0;
  function newKey() { return '_new_' + Date.now() + '_' + (++_key); }
  function addComponent() {
    setComponents(prev => [...prev, {
      _key:newKey(), seq:(prev.length+1)*10, code:'', name:'', formula:'',
      category:'Earning', pay_type:'Fixed', borne_by:'Employee',
      is_visible:true, is_tax_deductible:false, tax_limit:'', is_statutory:false,
      slip_description:'', notes:'',
    }]);
  }
  function updateComponent(idx, key, value) {
    setComponents(prev => { const copy=[...prev]; copy[idx] = { ...copy[idx], [key]:value }; return copy; });
  }
  function removeComponent(idx) { setComponents(prev => prev.filter((_,i) => i!==idx)); }
  function moveComponent(idx, dir) {
    setComponents(prev => {
      const j = idx + dir; if (j<0 || j>=prev.length) return prev;
      const copy = [...prev]; [copy[idx], copy[j]] = [copy[j], copy[idx]];
      copy.forEach((c,i) => { c.seq = (i+1)*10; });
      return copy;
    });
  }

  /* Simulation — computed live from current components + sim inputs */
  const resolvedById = useMemo(() => {
    const m = {}; for (const r of resolved) m[r.component_id] = r; return m;
  }, [resolved]);
  const simulation = useMemo(
    () => runSimulation(components, { ctc_annual: simCtc, monthly_fees: simFees, basic_monthly: simBasic }, resolvedById),
    [components, simCtc, simFees, simBasic, resolvedById]
  );

  const filteredSchemas = useMemo(() => {
    if (!search) return schemas;
    const s = search.toLowerCase();
    return schemas.filter(r =>
      (r.name||'').toLowerCase().includes(s) ||
      (r.code||'').toLowerCase().includes(s) ||
      (r.schema_type||'').toLowerCase().includes(s)
    );
  }, [schemas, search]);

  const columns = [
    { key:'code',        label:'Code',  sortable:true, width:'8%' },
    { key:'name',        label:'Name',  sortable:true, width:'28%' },
    { key:'schema_type', label:'Type',  sortable:true, width:'10%',
      render: v => <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4,
        background: v==='Retainer'?'rgba(191,95,255,0.15)':'rgba(59,143,244,0.15)',
        color:      v==='Retainer'?'#BF5FFF':'#3B8FF4' }}>{v}</span> },
    { key:'valid_from',  label:'From',  sortable:true, width:'10%', render: v => v?new Date(v).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'—' },
    { key:'valid_to',    label:'To',    sortable:true, width:'10%', render: v => v?new Date(v).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'—' },
    { key:'component_count', label:'Components', sortable:true, width:'10%', type:'number', align:'right' },
    { key:'employee_count',  label:'Employees',  sortable:true, width:'10%', type:'number', align:'right' },
    { key:'status',      label:'Status', sortable:true, width:'8%',
      render: v => <span className={`badge ${v==='Active'?'badge-green':'badge-gray'}`}>{v}</span> },
  ];

  if (mode === 'edit' && editRow) {
    return <SchemaEditor
      row={editRow} setRow={setEditRow}
      components={components} setComponents={setComponents}
      err={err} saving={saving}
      onSave={handleSave} onCancel={() => setMode('list')}
      addComponent={addComponent} updateComponent={updateComponent}
      removeComponent={removeComponent} moveComponent={moveComponent}
      simulation={simulation}
      simCtc={simCtc} setSimCtc={setSimCtc}
      simFees={simFees} setSimFees={setSimFees}
      simBasic={simBasic} setSimBasic={setSimBasic}
      simView={simView} setSimView={setSimView}
      showSim={showSim} setShowSim={setShowSim}
    />;
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', marginBottom:16, gap:12 }}>
        <input type="text" placeholder="Search by name, code, type..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex:1, maxWidth:400, padding:'8px 14px', borderRadius:6,
            border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.04)',
            color:'#E5E7EB', fontSize:13 }} />
        <button className="btn btn-primary" onClick={openNew}>+ New Schema</button>
        <button className="btn btn-secondary"
          onClick={() => { window.location.hash = '#/salary-schemas/component-masters'; }}
          title="Maintain component-wise rates by region and date range">
          Component Masters
        </button>
      </div>
      <DataTable columns={columns} data={filteredSchemas}
        actions={[
          { label:'Edit',   icon:'edit',   onClick:openEdit },
          { label:'Copy',   icon:'copy',   onClick:handleDuplicate },
          { label:'Delete', icon:'delete', onClick:handleDelete },
        ]}
        onRowClick={openEdit} loading={loading}
        emptyMessage="No salary schemas yet. Click + New Schema to create one."
        emptyIcon={'\u{1F4B0}'} />
    </div>
  );
}

/* ═══════════════════════════════ Full-page editor ═══════════════════════════════ */

function SchemaEditor({
  row, setRow, components, setComponents, err, saving, onSave, onCancel,
  addComponent, updateComponent, removeComponent, moveComponent,
  simulation, simCtc, setSimCtc, simFees, setSimFees,
  simBasic, setSimBasic, simView, setSimView,
  showSim, setShowSim,
}) {
  /* Details-row expand state — keyed by component _key so each row can
     toggle independently. Closed by default — keeps the grid compact. */
  const [detailsOpen, setDetailsOpen] = React.useState({});
  const toggleDetails = (k) => setDetailsOpen(s => ({ ...s, [k]: !s[k] }));
  // Accordion state for the schema-header card. Open on new schemas, folded
  // on existing so the components area gets more vertical real estate.
  const [headerOpen, setHeaderOpen] = React.useState(!!row._new);

  // Confirmation wrapper around removeComponent — components aren't persisted
  // until Save, but clicking delete by accident still loses in-progress edits.
  async function confirmRemove(idx, c) {
    const label = c.name || c.code || 'this row';
    const ok = await toast.confirm({
      title: 'Delete component?',
      body: `Remove "${label}" from the schema? This takes effect when you click Save Schema.`,
      confirmLabel: 'Delete component',
      danger: true,
    });
    if (ok) removeComponent(idx);
  }

  return (
    <div style={{ display:'grid', gridTemplateColumns: showSim ? 'minmax(0,1fr) 360px' : 'minmax(0,1fr)', gap:16 }}>
      <div>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
          <button className="btn btn-secondary" onClick={onCancel}>{'\u2190'} Back</button>
          <h2 style={{ margin:0, fontSize:18, flex:1 }}>
            {row._new ? 'New Salary Schema' : 'Edit Schema'} {row.name && !row._new && (
              <span style={{ color:'#94A3B8', fontWeight:400, fontSize:14, marginLeft:6 }}>— {row.name}</span>
            )}
          </h2>
          <button className="btn btn-secondary" onClick={() => setShowSim(s => !s)}>
            {showSim ? 'Hide Simulator' : 'Show Simulator'}
          </button>
          {!row._new && row.id && (
            <button className="btn btn-secondary"
              onClick={() => { window.location.hash = `#/salary-schemas/${row.id}/component-masters`; }}
              title="Maintain component-wise rates for this schema. Schema is defaulted and locked.">
              Component Masters
            </button>
          )}
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Schema'}
          </button>
        </div>

        {err && (
          <div style={{ background:'rgba(239,68,68,0.12)', color:'#EF4444',
                        padding:'10px 14px', borderRadius:6, marginBottom:12, fontSize:13 }}>{err}</div>
        )}

        {/* Schema fields — foldable so the components table gets more room */}
        <div style={cardStyle}>
          <button type="button" onClick={() => setHeaderOpen(o => !o)}
            style={accHeaderStyle}
            title={headerOpen ? 'Fold schema header to give the components more room' : 'Expand schema header'}>
            <AccChevron open={headerOpen} />
            <span style={{ fontSize:13, fontWeight:600, color:'#E5E7EB', marginLeft:8 }}>Schema Details</span>
            {!headerOpen && (
              <span style={{ marginLeft:12, fontSize:12, color:'#94A3B8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {(row.code || '—')} · {(row.name || '(unnamed)')} · {row.schema_type || '—'} · <em style={{ color: row.status==='Active'?'#10B981':'#94A3B8' }}>{row.status || '—'}</em>
              </span>
            )}
          </button>
          {headerOpen && (
            <div style={{ marginTop:12 }}>
              <div className="form-row">
                <FormField label="Code" name="code" value={row.code} onChange={(n,v) => setRow(p => ({...p, code:v}))} required />
                <FormField label="Name" name="name" value={row.name} onChange={(n,v) => setRow(p => ({...p, name:v}))} required />
                <FormField label="Type" name="schema_type" type="select" value={row.schema_type}
                  onChange={(n,v) => setRow(p => ({...p, schema_type:v}))} options={SCHEMA_TYPES} />
              </div>
              <div className="form-row">
                <FormField label="Valid From" name="valid_from" type="date" value={row.valid_from} onChange={(n,v) => setRow(p => ({...p, valid_from:v}))} />
                <FormField label="Valid To"   name="valid_to"   type="date" value={row.valid_to}   onChange={(n,v) => setRow(p => ({...p, valid_to:v}))} />
                <FormField label="Status" name="status" type="select" value={row.status}
                  onChange={(n,v) => setRow(p => ({...p, status:v}))} options={STATUS_OPTIONS} />
              </div>
              <FormField label="Description" name="description" type="textarea" value={row.description || ''}
                onChange={(n,v) => setRow(p => ({...p, description:v}))} />
            </div>
          )}
        </div>

        {/* Components editor */}
        <div style={{ ...cardStyle, marginTop:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <h3 style={{ margin:0, fontSize:14 }}>
              Components <span style={{ color:'#94A3B8', fontSize:12, marginLeft:6 }}>({components.length})</span>
            </h3>
            <button className="btn btn-sm" onClick={addComponent}>+ Add Component</button>
          </div>

          {components.length === 0 ? (
            <div style={emptyStyle}>No components yet. Click + Add Component to start.</div>
          ) : (
            <div style={{ overflowX:'auto', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8 }}>
              <table style={tblStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width:56 }}  title="Sequence — controls the computation order. Lower runs first.">Seq</th>
                    <th style={{ ...thStyle, width:110 }} title="Short code used in formulas (e.g. BASIC, HRA). Case-insensitive when referenced.">Code</th>
                    <th style={{ ...thStyle, width:'18%' }} title="Human-readable component name shown to admins.">Name</th>
                    <th style={{ ...thStyle }} title="Formula or sum expression. Reference other components by their code. Examples: 0.5 * basic, basic + hra + spl">Formula</th>
                    <th style={{ ...thStyle, width:150 }} title="Earning / Deduction / Employer Contribution / Tax / Subtotal — drives how it's aggregated in CTC and payslip.">Category</th>
                    <th style={{ ...thStyle, width:110 }} title="Fixed vs Variable, and Direct vs Indirect for taxes.">Pay Type</th>
                    <th style={{ ...thStyle, width:110 }} title="Who bears this component — Employee (visible on their slip) or Company (employer cost only).">Borne By</th>
                    <th style={{ ...thStyle, width:110, textAlign:'right' }} title="Actions — re-order, view details, delete.">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {components.map((c, idx) => {
                    const cat  = CATEGORY_COLOR[c.category] || CATEGORY_COLOR.Earning;
                    const key  = c._key || c.id || idx;
                    const open = !!detailsOpen[key];
                    return (
                      <React.Fragment key={key}>
                        <tr style={{ borderTop:'1px solid rgba(255,255,255,0.06)' }}>
                          <td style={tdStyle}><input type="number" value={c.seq} onChange={e => updateComponent(idx,'seq',e.target.value)} style={{ ...inputStyle, width:'100%' }} title="Sequence" /></td>
                          <td style={tdStyle}><input type="text"   value={c.code} onChange={e => updateComponent(idx,'code',e.target.value)} style={{ ...inputStyle, width:'100%' }} placeholder="BASIC" /></td>
                          <td style={tdStyle}><input type="text"   value={c.name} onChange={e => updateComponent(idx,'name',e.target.value)} style={{ ...inputStyle, width:'100%' }} placeholder="Basic Salary" /></td>
                          <td style={tdStyle}>
                            <input type="text" value={c.formula||''} onChange={e => updateComponent(idx,'formula',e.target.value)}
                              style={{ ...inputStyle, width:'100%', fontFamily:'ui-monospace, monospace' }}
                              placeholder={c.category==='Subtotal' ? 'basic + hra + spl' : '0.5 * basic'} />
                          </td>
                          <td style={tdStyle}>
                            <select value={c.category} onChange={e => updateComponent(idx,'category',e.target.value)}
                              style={{ ...inputStyle, width:'100%', color:cat.color, background:cat.bg }}>
                              {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td style={tdStyle}>
                            <select value={c.pay_type} onChange={e => updateComponent(idx,'pay_type',e.target.value)} style={{ ...inputStyle, width:'100%' }}>
                              {PAY_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td style={tdStyle}>
                            <select value={c.borne_by} onChange={e => updateComponent(idx,'borne_by',e.target.value)} style={{ ...inputStyle, width:'100%' }}>
                              {BORNE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>
                            <div style={{ display:'inline-flex', gap:4 }}>
                              <button type="button" onClick={() => moveComponent(idx,-1)} style={iconBtn} title="Move up">↑</button>
                              <button type="button" onClick={() => moveComponent(idx,+1)} style={iconBtn} title="Move down">↓</button>
                              <button type="button" onClick={() => toggleDetails(key)}
                                style={{ ...iconBtn, color: open ? '#00E0FF' : '#94A3B8' }}
                                title={open ? 'Hide details' : 'Show details (slip text, flags, tax limit, notes)'}>
                                <MagnifyIcon />
                              </button>
                              <button type="button" onClick={() => confirmRemove(idx, c)}
                                style={deleteBtn} title={`Delete component${c.name ? ' "'+c.name+'"' : ''}`}>
                                <TrashIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {open && (
                          <tr style={{ background:'rgba(0,224,255,0.04)' }}>
                            <td colSpan={8} style={{ padding:'12px 14px', borderTop:'1px dashed rgba(0,224,255,0.2)' }}>
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:10 }}>
                                <label style={detLblStyle} title="The exact text that appears on the employee's payslip for this component.">
                                  <span>Payslip Description</span>
                                  <input type="text" value={c.slip_description||''} onChange={e => updateComponent(idx,'slip_description',e.target.value)}
                                    style={{ ...inputStyle, width:'100%' }} placeholder="Text shown on the payslip line" />
                                </label>
                                <label style={detLblStyle} title="Internal admin notes — never shown to the employee.">
                                  <span>Internal Notes</span>
                                  <input type="text" value={c.notes||''} onChange={e => updateComponent(idx,'notes',e.target.value)}
                                    style={{ ...inputStyle, width:'100%' }} placeholder="Admin-only notes" />
                                </label>
                              </div>
                              <div style={{ display:'flex', gap:18, alignItems:'center', flexWrap:'wrap', fontSize:12, color:'#94A3B8' }}>
                                <label style={chkLbl} title="If unchecked the component is used in math but never shown on the employee's slip.">
                                  <input type="checkbox" checked={!!c.is_visible} onChange={e => updateComponent(idx,'is_visible',e.target.checked)} /> Visible on payslip
                                </label>
                                <label style={chkLbl} title="Component reduces taxable income up to the limit. Common for HRA, LTA, 80C allowances.">
                                  <input type="checkbox" checked={!!c.is_tax_deductible} onChange={e => updateComponent(idx,'is_tax_deductible',e.target.checked)} /> Tax deductible
                                  {c.is_tax_deductible && (
                                    <input type="number" value={c.tax_limit||''} onChange={e => updateComponent(idx,'tax_limit',e.target.value)}
                                      style={{ ...inputStyle, width:140, marginLeft:8 }} placeholder="Annual limit ₹" title="Annual exemption limit in rupees" />
                                  )}
                                </label>
                                <label style={chkLbl} title="Mandated by statute — PF, ESI, PT, income tax, gratuity, etc.">
                                  <input type="checkbox" checked={!!c.is_statutory} onChange={e => updateComponent(idx,'is_statutory',e.target.checked)} /> Statutory
                                </label>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ─── Simulation panel ─── */}
      {showSim && (
        <div style={{ position:'sticky', top:16, alignSelf:'start' }}>
          <div style={cardStyle}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
              <h3 style={{ margin:0, fontSize:14 }}>Simulator</h3>
              {/* Monthly / Yearly view toggle. Yearly multiplies the totals
                  and breakdown by 12 (per-month basic value × 12 etc.). */}
              <div style={{ display:'inline-flex', borderRadius:6, border:'1px solid rgba(255,255,255,0.10)', overflow:'hidden' }}>
                {['monthly','yearly'].map(v => (
                  <button key={v} onClick={() => setSimView(v)}
                    style={{
                      padding:'4px 10px', fontSize:11, fontWeight:600,
                      border:'none', cursor:'pointer',
                      background: simView === v ? 'rgba(0,224,255,0.18)' : 'transparent',
                      color: simView === v ? '#00E0FF' : '#94A3B8',
                      letterSpacing:'0.4px', textTransform:'uppercase',
                    }}>{v}</button>
                ))}
              </div>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12 }}>
              {row.schema_type === 'Retainer' ? (
                <label style={{ fontSize:12, color:'#94A3B8' }}>
                  Monthly Retainer Fees (₹)
                  <input type="number" value={simFees} onChange={e => setSimFees(Number(e.target.value)||0)}
                    style={{ ...inputStyle, marginTop:4 }} />
                </label>
              ) : (
                <>
                  <label style={{ fontSize:12, color:'#94A3B8' }}>
                    Annual CTC (₹)
                    <input type="number" value={simCtc} onChange={e => setSimCtc(Number(e.target.value)||0)}
                      style={{ ...inputStyle, marginTop:4 }} />
                  </label>
                  <label style={{ fontSize:12, color:'#94A3B8' }}>
                    Basic Value (per month) (₹)
                    <input type="number" value={simBasic || ''} onChange={e => setSimBasic(Number(e.target.value)||0)}
                      placeholder="0 — leave blank to use formula / master"
                      style={{ ...inputStyle, marginTop:4 }} />
                  </label>
                </>
              )}
            </div>

            {/* Totals — switch between monthly and annual based on view.
                Component values are computed monthly internally; gross /
                deductions / tax / company_cost are stored as annualised
                figures (×12). For the monthly view we divide back by 12. */}
            {(() => { const yearly = simView === 'yearly'; const mul = yearly ? 1 : 1/12;
              const lbl = yearly ? 'Annual' : 'Monthly';
              return (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:14 }}>
                  <Totl label={`${lbl} Gross`}      value={simulation.totals.gross * mul}             color="#10B981" />
                  <Totl label={`${lbl} Deductions`} value={-simulation.totals.total_deductions * mul} color="#EF4444" />
                  <Totl label={`${lbl} Tax`}        value={-simulation.totals.total_tax * mul}        color="#F59E0B" />
                  <Totl label={`${lbl} Net`}        value={simulation.totals.net * mul}               color="#00E0FF" big />
                  <Totl label="Employer Cost"       value={simulation.totals.company_cost * mul}      color="#3B8FF4" />
                  <Totl label={yearly ? 'Total CTC' : 'Monthly CTC'} value={simulation.totals.ctc * mul} color="#BF5FFF" big />
                </div>
              );
            })()}

            {/* Per-component breakdown */}
            <div style={{ maxHeight:400, overflowY:'auto', border:'1px solid rgba(255,255,255,0.06)', borderRadius:6, padding:6 }}>
              {simulation.results.length === 0 ? (
                <div style={{ color:'#64748B', fontSize:12, padding:12, textAlign:'center' }}>
                  Add components to see values
                </div>
              ) : simulation.results.map((r, i) => {
                const cat = CATEGORY_COLOR[r.category] || CATEGORY_COLOR.Earning;
                const monthly = Number(r._computed) || 0;
                // Per-component value follows the simulator's view toggle.
                const shown = simView === 'yearly' ? monthly * 12 : monthly;
                const tag = r._source === 'manual_basic'
                  ? 'manual'
                  : r._source === 'master'
                    ? `L${r._matchLevel || '?'}`
                    : (r._source === 'formula' ? 'fx' : '—');
                const tagColor = r._source === 'master'
                  ? '#10B981'
                  : r._source === 'manual_basic' ? '#F59E0B' : '#94A3B8';
                return (
                  <div key={i} style={{
                    display:'grid', gridTemplateColumns:'1fr auto auto', gap:8, padding:'4px 6px',
                    fontSize:12, borderBottom:'1px solid rgba(255,255,255,0.04)', alignItems:'center',
                  }}>
                    <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <span style={{ display:'inline-block', width:8, height:8, borderRadius:2,
                        background:cat.color, marginRight:6, opacity:0.8 }}></span>
                      {r.name}
                    </div>
                    <span style={{
                      fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4,
                      background:'rgba(255,255,255,0.04)', color: tagColor, letterSpacing:'0.4px',
                    }} title={r._source === 'master' ? 'Resolved from Component Master' : 'Computed from formula'}>
                      {tag}
                    </span>
                    <div style={{ fontFamily:'ui-monospace, monospace', color: shown>=0 ? '#E5E7EB' : '#EF4444' }}>
                      {fmtINR(shown)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize:10.5, color:'#64748B', marginTop:8, lineHeight:1.4 }}>
              Formulas can reference <code>ctc_annual</code>, <code>monthly_fees</code>, or any earlier component by its lowercase code.
              Helpers: <code>min, max, round, slab_tax()</code>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Totl({ label, value, color, big }) {
  return (
    <div style={{
      background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)',
      borderRadius:6, padding:'8px 10px',
    }}>
      <div style={{ fontSize:10, color:'#94A3B8', marginBottom:2, textTransform:'uppercase', letterSpacing:0.5 }}>{label}</div>
      <div style={{ color, fontSize: big?15:13, fontWeight:700, fontFamily:'ui-monospace, monospace' }}>{fmtINR(value)}</div>
    </div>
  );
}

/* shared styles */
const cardStyle  = { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:10, padding:16 };
const inputStyle = { padding:'6px 10px', borderRadius:5, border:'1px solid rgba(255,255,255,0.10)',
                     background:'rgba(255,255,255,0.04)', color:'#E5E7EB', fontSize:12.5, outline:'none', width:'100%' };
const iconBtn    = { width:26, height:26, padding:0, display:'inline-flex', alignItems:'center', justifyContent:'center',
                     borderRadius:5, border:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.04)',
                     color:'#94A3B8', cursor:'pointer', fontSize:14 };
const chkLbl     = { display:'inline-flex', alignItems:'center', gap:5, cursor:'pointer' };
const emptyStyle = { padding:24, textAlign:'center', color:'#64748B', fontSize:13,
                     background:'rgba(255,255,255,0.03)', borderRadius:8, border:'1px dashed rgba(255,255,255,0.08)' };

/* ── Table styles for the components editor ── */
const tblStyle = {
  width:'100%', borderCollapse:'separate', borderSpacing:0, tableLayout:'fixed',
  fontSize:12.5,
};
const thStyle = {
  padding:'10px 10px', textAlign:'left',
  background:'rgba(255,255,255,0.04)',
  color:'#94A3B8', fontSize:11, fontWeight:600,
  textTransform:'uppercase', letterSpacing:'0.4px',
  borderBottom:'1px solid rgba(255,255,255,0.10)',
  cursor:'help',
};
const tdStyle = {
  padding:'6px 8px', verticalAlign:'middle',
};
const detLblStyle = {
  display:'flex', flexDirection:'column', gap:4,
  fontSize:11, color:'#94A3B8', letterSpacing:'0.2px',
};

/* Accordion header for the schema-details card — click to fold / unfold. */
const accHeaderStyle = {
  display:'flex', alignItems:'center', gap:2,
  width:'100%', padding:'2px 4px', margin:0,
  background:'transparent', border:'none', cursor:'pointer', textAlign:'left',
};
function AccChevron({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
         style={{ color:'#94A3B8', transition:'transform 0.2s ease', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// Red-tinted delete button — stands out more than the previous subtle ×.
const deleteBtn = {
  width:28, height:28, padding:0, display:'inline-flex', alignItems:'center', justifyContent:'center',
  borderRadius:5, border:'1px solid rgba(239,68,68,0.35)',
  background:'rgba(239,68,68,0.10)', color:'#EF4444', cursor:'pointer',
};
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

/* ── Modern magnifying-glass icon (SVG) for the "Details" action ── */
function MagnifyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.5" y2="16.5" />
    </svg>
  );
}
